import React, { useEffect, useState } from "react";
import { Controller, FormProvider } from "react-hook-form";
import { AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { EndPoints } from "../../../Constants/endpoints";
import { FrequencyType, MealType } from "../../../interface/GeneralResponse";
import { OptionType } from "../../../interface/OptionTypes";
import AddPrescriptionSearchDrug from "../AddPrescriptionSearchDrug";
import PrescriptionDrugItem from "./PrescriptionDrugItem";
import PrescriptionFormActions from "./PrescriptionFormActions";
import { PrescriptionBaseProps } from "../types/prescription.types";
import { usePrescriptionForm } from "../hooks/usePrescriptionForm";
import { usePrescriptionDrugs } from "../hooks/usePrescriptionDrugs";
import { useToast } from "../../../Services/ToastContext";
import { GeneralInput } from "../../../components";
import PatientInfoCard from "./PatientInfoCard";
import { useGetData } from "../../../hooks";

const PrescriptionBaseForm: React.FC<PrescriptionBaseProps> = ({
  patientId,
  initialData,
  onSubmit,
  config,
  isLoading = false,
}) => {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const [isInitialized, setIsInitialized] = useState(false);

  const { data: mealTypesResponse } = useGetData<MealType[]>(
    EndPoints.GetMealTypes,
  );
  const { data: frequencyTypesResponse } = useGetData<FrequencyType[]>(
    EndPoints.FrequencyTypes,
  );

  const mealTypesData = mealTypesResponse?.Data || [];
  const frequencyTypesData = frequencyTypesResponse?.Data || [];

  const currentLanguage = i18n.language?.slice(0, 2);

  const mealTypeOptions: OptionType[] = mealTypesData.map((mealType) => ({
    key: mealType.Code,
    label: currentLanguage === "ar" ? mealType.NameAr : mealType.NameEn,
    value: mealType.Code,
  }));

  const frequencyTypeOptions: OptionType[] = frequencyTypesData.map(
    (freqType) => ({
      key: freqType.Code,
      label: currentLanguage === "ar" ? freqType.NameAr : freqType.NameEn,
      value: freqType.Code,
    }),
  );

  const timesOptions: OptionType[] = Array.from({ length: 8 }, (_, i) => ({
    key: i + 1,
    label: (i + 1).toString(),
    value: i + 1,
  }));

  const methods = usePrescriptionForm(initialData);
  const { control, handleSubmit, reset } = methods;

  const {
    selectedDrugs,
    fields,
    handleDrugSelect,
    handleRemoveDrug,
    resetDrugs,
    setInitialDrugs,
  } = usePrescriptionDrugs(methods, showToast);
  useEffect(() => {
    if (initialData?.Drugs && initialData.Drugs.length > 0 && !isInitialized) {
      const mappedDrugs = initialData.Drugs.map((drug: any, index: number) => {
        const isCustom = !drug.PackageId && !!drug.Name;

        return {
          ...drug,
          DrugName: drug.DrugName || drug.Name || "Drug",
          _tempId: isCustom ? `edit_${index}_${drug.Name}` : undefined,
          isCustom,
        };
      });

      setInitialDrugs(mappedDrugs);
      setIsInitialized(true);
    }
  }, [initialData, setInitialDrugs, isInitialized]);

  const handleFormSubmit = (formData: any) => {
    const drugsData = selectedDrugs?.map((drug, index) => {
      const formDrug = methods.getValues().Drugs[index];

      const drugData: any = {
        Frequency: formDrug?.Frequency || "",
        SelectedDays: formDrug?.SelectedDays || [],
        Meal: formDrug?.Meal || "",
        Sleep: formDrug?.Sleep || false,
        Times: formDrug?.Times || null,
        Duration: formDrug?.Duration || null,
        Instructions: formDrug?.Instructions || "",
        DosageNum: formDrug?.DosageNum || drug.DosageNum || null,
        DosageUnit: formDrug?.DosageUnit || drug.DosageUnit || undefined,
      };

      if (drug.PackageId) {
        drugData.PackageId = drug.PackageId;
      } else if (drug.Name) {
        drugData.Name = drug.Name;
      }

      return drugData;
    });

    onSubmit({
      Case: formData?.Case,
      Drugs: drugsData,
    });
  };

  const itemVariants = {
    hidden: { opacity: 0, x: 50, transition: { duration: 0.4 } },
    visible: { opacity: 1, x: 0, transition: { duration: 0.4 } },
    exit: { opacity: 0, x: 50, transition: { duration: 0.4 } },
  };

  return (
    <div className="box flex h-full flex-col p-6">
      <h1 className="font-bold">{config.title}</h1>
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className="flex grow flex-col gap-6 overflow-auto md:flex-row"
        >
          <AddPrescriptionSearchDrug
            onDrugSelect={handleDrugSelect}
            selectedDrugs={selectedDrugs}
          />

          <div className="flex w-full flex-col gap-4 lg:w-3/4 lg:px-2">
            {config.showPatientInfo && patientId && <PatientInfoCard />}

            <div>
              <div className="text-primary-text mb-4 block font-bold">
                {t("prescription_details")}
              </div>
              <Controller
                name="Case"
                control={control}
                render={({ field }) => (
                  <GeneralInput
                    label={config.caseLabel}
                    placeholder={config.caseLabel}
                    required
                    maxLength={200}
                    type="text"
                    {...field}
                  />
                )}
              />
            </div>

            {fields.length === 0 ? (
              <p className="text-body-text grow">{t("no_drugs_selected")}</p>
            ) : (
              <div className="flex grow flex-col space-y-4 overflow-auto">
                <AnimatePresence>
                  {fields.map((field: any, index) => {
                    const isCustomDrug =
                      !field.PackageId && (field.Name || field._tempId);

                    let drug;
                    if (isCustomDrug) {
                      drug = selectedDrugs.find(
                        (d) =>
                          (d.Name && d.Name === field.Name) ||
                          (d._tempId && d._tempId === field._tempId),
                      );
                    } else {
                      drug = selectedDrugs.find(
                        (d) => d.PackageId === field.PackageId,
                      );
                    }

                    if (!drug) {
                      return null;
                    }

                    const identifier = isCustomDrug
                      ? field.Name || field._tempId
                      : field.PackageId;

                    return (
                      <PrescriptionDrugItem
                        key={field.id}
                        field={field}
                        index={index}
                        drug={drug}
                        isCustomDrug={isCustomDrug}
                        identifier={identifier}
                        onRemove={handleRemoveDrug}
                        mealTypeOptions={mealTypeOptions}
                        frequencyTypeOptions={frequencyTypeOptions}
                        timesOptions={timesOptions}
                        currentLanguage={currentLanguage}
                        maxInstructionsLength={
                          config.maxInstructionsLength || 500
                        }
                        itemVariants={itemVariants}
                      />
                    );
                  })}
                </AnimatePresence>
              </div>
            )}

            <PrescriptionFormActions
              onDiscard={() => {
                reset({ Case: "", Drugs: [] });
                resetDrugs();
              }}
              submitButtonText={config.submitButtonText}
              isDisabled={fields.length === 0 || isLoading}
            />
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default PrescriptionBaseForm;
